import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { XIcon } from 'lucide-react';
import { createContext, use, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button/Button';

export function FileCard(fileCardProps: ComponentProps<'div'>) {
  const { className, ...props } = fileCardProps;
  return (
    <div
      data-slot="file-card"
      className={cn(
        'bg-background hover:bg-card/75 border-border-primary transition-outline outline-focus-ring relative flex flex-col gap-2 rounded-lg border p-2 opacity-100 outline-0 transition-colors duration-150 focus-visible:outline-3 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 [&_svg]:pointer-events-none',
        className
      )}
      {...props}
    />
  );
}

export function FileCardTitle(fileCardTitleProps: ComponentProps<'h5'>) {
  const { className, ...props } = fileCardTitleProps;
  return <h5 data-slot="file-card-title" className={cn('text-foreground w-full truncate text-sm whitespace-nowrap', className)} {...props} />;
}

export function FileCardContent(fileCardContentProps: ComponentProps<'div'>) {
  const { className, ...props } = fileCardContentProps;
  return <div data-slot="file-card-content" className={cn('relative flex items-center gap-2', className)} {...props} />;
}

export interface FileCardPreviewContextProps {
  hasError: boolean;
  triggerError: () => void;
}
export const FileCardPreviewContext = createContext<FileCardPreviewContextProps>({
  hasError: false,
  triggerError: () => {},
});

export function FileCardPreview(fileCardPreviewProps: ComponentProps<'div'>) {
  const { className, children, ...props } = fileCardPreviewProps;
  const [hasError, setHasError] = useState<boolean>(false);
  const triggerError = () => setHasError(true);

  const contextValue: FileCardPreviewContextProps = { hasError, triggerError };

  return (
    <div data-slot="file-card-preview" className={cn('border-border-primary bg-accent relative h-24 w-full overflow-hidden rounded-md border', className)} {...props}>
      <FileCardPreviewContext value={contextValue}>{children}</FileCardPreviewContext>
    </div>
  );
}

export function FileCardPreviewImage(fileCardPreviewImageProps: ComponentProps<'img'>) {
  const { src, className, ...props } = fileCardPreviewImageProps;
  const { hasError, triggerError } = use(FileCardPreviewContext);

  useEffect(() => {
    if (!src) {
      triggerError();
    }
  }, [src, triggerError]);

  if (hasError || !src) {
    return null;
  }

  return <img src={src} data-slot="file-card-preview-image" aria-label={'Preview'} alt={'Preview'} onError={triggerError} className={cn('h-full w-full object-cover', className)} {...props} />;
}

export function FileCardPreviewFallback(fileCardPreviewFallbackProps: ComponentProps<'span'>) {
  const { className, ...props } = fileCardPreviewFallbackProps;
  const { hasError } = use(FileCardPreviewContext);
  if (!hasError) return null;
  return (
    <span
      aria-label="Fallback preview"
      data-slot="file-card-preview-fallback"
      className={cn('text-muted-foreground *:stroke-muted-foreground flex h-full w-full items-center justify-center object-cover', className)}
      {...props}
    />
  );
}

export function FileCardActionButton(fileCardActionButtonProps: ComponentProps<typeof Button>) {
  const { className, ...props } = fileCardActionButtonProps;
  return (
    <Button
      type="button"
      variant={'tertiary'}
      variantColor={'destructive'}
      size="sm"
      className={cn('absolute -top-2 -right-2 z-20 h-6 min-h-6 w-6 min-w-6 rounded-full border-2 shadow-none', className)}
      aria-label="Remove item"
      {...props}
    >
      <XIcon className="size-3" />
    </Button>
  );
}
